/* ============================================================================
 * ตัวเลือกกลางของทรัพย์ — ใช้ร่วมกันทั้งหน้ารวมทรัพย์ (assets.js)
 * และหน้าเพิ่ม/แก้ไขทรัพย์ (asset_create.js)
 *
 * เดิมสองหน้าประกาศชุดประเภททรัพย์แยกกันแล้วชุดไม่ตรงกัน (หน้าเพิ่มมี townhouse /
 * new_project แต่หน้ารวมไม่มี ส่วนหน้ารวมมี twin_house แต่หน้าเพิ่มไม่มี) ทำให้
 * ทรัพย์บางประเภทกรองหรือแสดงผลในหน้ารวมไม่ได้ จึงย้ายมาไว้ที่เดียว
 *
 *   icon = ฟอนต์ไอคอนของโปรเจกต์ (fa-light)
 *   grad = พื้นหลังไล่เฉดที่ใช้แทนรูปตอนทรัพย์ยังไม่มีรูปจริง
 * ========================================================================== */

/* ประเภทประกาศ — เช่า / ขาย (เลือกพร้อมกันได้) */
export const DEALS = [
  { value: "rent", label: "for_rent" },
  { value: "sale", label: "for_sale" },
];

/* ============================================================================
 * สถานะทรัพย์ — ชุดกลางที่ทุกหน้าต้องใช้ร่วมกัน
 *
 * value ต้องตรงกับ ASSET_STATUS ที่ backend รับ (modules/admin/asset/asset.constant.js)
 * เดิมหน้ารวมทรัพย์ / หน้าโพสต์ / แดชบอร์ด / รายงาน ต่างคนต่างประกาศชุดสีของตัวเอง
 * ทำให้ทรัพย์ใบเดียวกันขึ้นคนละสีคนละคำในแต่ละหน้า จึงย้ายมาไว้ที่เดียว
 *
 *   dot   = สีวงกลมนำหน้า (โทนตามที่ตกลงไว้ : เขียว เหลือง น้ำเงิน แดง ม่วง ดำ)
 *   color = สีตัวอักษรบนป้าย — เข้มกว่า dot หนึ่งระดับให้อ่านออกบนพื้นอ่อน
 *   tint  = สีพื้นของป้าย
 * ========================================================================== */
export const PROPERTY_STATUS = [
  { value: "available", label: "status_available", desc: "status_available_desc", dot: "#16a34a", color: "#15803d", tint: "#dcfce7" },
  { value: "reserved", label: "status_reserved", desc: "status_reserved_desc", dot: "#eab308", color: "#a16207", tint: "#fef9c3" },
  { value: "processing", label: "status_processing", desc: "status_processing_desc", dot: "#2563eb", color: "#1d4ed8", tint: "#dbeafe" },
  { value: "sold", label: "status_sold", desc: "status_sold_desc", dot: "#dc2626", color: "#b91c1c", tint: "#fee2e2" },
  { value: "rented", label: "status_rented", desc: "status_rented_desc", dot: "#9333ea", color: "#7e22ce", tint: "#f3e8ff" },
  { value: "off_market", label: "status_off_market", desc: "status_off_market_desc", dot: "#111827", color: "#111827", tint: "#e5e7eb" },
];

export const DEFAULT_STATUS = "available";

/* ทรัพย์ที่ลงไว้ก่อนแยกสถานะ closed ออกเป็น rented / off_market อาจยังค้างค่าเดิมอยู่
   (เช่นข้อมูลที่ค้างในแท็บที่เปิดทิ้งไว้) — map ให้แสดงผลได้แทนที่จะขึ้นป้ายเปล่า */
const LEGACY_STATUS = { closed: "off_market" };

export const statusMeta = (value) => {
  const key = LEGACY_STATUS[value] || value;
  return PROPERTY_STATUS.find((e) => e.value === key) || PROPERTY_STATUS[0];
};

/* รูปแบบ object สำหรับหน้าที่อ้างถึงสถานะด้วย key ตรง ๆ (STATUS_META[d.status]) */
export const STATUS_META = Object.fromEntries(PROPERTY_STATUS.map((e) => [e.value, e]));

/* ประเภททรัพย์ — value ต้องตรงกับที่ backend เก็บ */
export const PROPERTY_TYPES = [
  { value: "condo", label: "type_condo", icon: "\uf1ad", grad: "linear-gradient(150deg,#dfeaf5,#c3d5e8)" },
  { value: "house", label: "type_house", icon: "\uf015", grad: "linear-gradient(150deg,#e9efe3,#d3e2c6)" },
  { value: "townhouse", label: "type_townhouse", icon: "\uf64f", grad: "linear-gradient(150deg,#f1ebe2,#dfd3c3)" },
  { value: "townhome", label: "type_townhome", icon: "\uf64f", grad: "linear-gradient(150deg,#f1ebe2,#dfd3c3)" },
  { value: "twin_house", label: "type_twin_house", icon: "\uf015", grad: "linear-gradient(150deg,#eae7f3,#d4cfe6)" },
  { value: "home_office", label: "type_home_office", icon: "\uf0b1", grad: "linear-gradient(150deg,#e4eef0,#c7dde0)" },
  { value: "commercial", label: "type_commercial", icon: "\uf54e", grad: "linear-gradient(150deg,#f3e8e8,#e2cccc)" },
  { value: "warehouse", label: "type_warehouse", icon: "\uf494", grad: "linear-gradient(150deg,#eceef0,#d2d7dc)" },
  { value: "land", label: "type_land", icon: "\uf1bb", grad: "linear-gradient(150deg,#e7f1e4,#cbe3c6)" },
  { value: "new_project", label: "type_new_project", icon: "\uf66f", grad: "linear-gradient(150deg,#e9e6f0,#cdc7de)" },
];

/* ประเภทที่คิดขนาดเป็นตารางวา (ที่ดิน) แทนพื้นที่ใช้สอยตารางเมตร */
export const isLandType = (value) => value === "land";

/* ประเภทแนวราบ — มีทั้งพื้นที่ดิน (ตร.ว.) และพื้นที่ใช้สอย (ตร.ม.) และนับจำนวนชั้นของตัวบ้านได้
   ต้องตรงกับ HOUSE_CATEGORY_CODES ฝั่ง backend (modules/admin/asset/asset.constant.js) */
export const HOUSE_TYPES = ["house", "twin_house", "townhouse", "townhome"];
export const isHouseType = (value) => HOUSE_TYPES.includes(value);

export const typeMeta = (value) => PROPERTY_TYPES.find((e) => e.value === value) || PROPERTY_TYPES[0];

/* ทิศของทรัพย์ — ใช้ทั้งในฟอร์มลงทรัพย์และหน้ารายละเอียดฝั่งเว็บลูกค้า */
export const DIRECTIONS = [
  { value: "north", label: "dir_north" },
  { value: "northeast", label: "dir_northeast" },
  { value: "east", label: "dir_east" },
  { value: "southeast", label: "dir_southeast" },
  { value: "south", label: "dir_south" },
  { value: "southwest", label: "dir_southwest" },
  { value: "west", label: "dir_west" },
  { value: "northwest", label: "dir_northwest" },
];

/* ============================================================================
 * ที่จอดรถ — ยึด "ประเภทที่จอดได้" เป็นหลัก จำนวนคันเป็นข้อมูลเสริม
 *
 * เดิมฟอร์มให้กรอกจำนวนคัน (parking) แต่คนลงประกาศไม่ได้นับคันจริง สิ่งที่ลูกค้าถาม
 * คือจอดรถยนต์ได้ไหม จอดมอเตอร์ไซค์ได้ไหม จึงเปลี่ยนเป็นติ๊กประเภท (ติ๊กได้ทั้งคู่)
 *
 * ติ๊กประเภทแล้วระบุ "จำนวนคัน" ต่อได้ แต่ไม่บังคับ — เหตุผลเดิมยังอยู่ คือคนลงประกาศ
 * ส่วนใหญ่ตอบได้แค่จอดได้/ไม่ได้ ใครที่รู้จำนวนจริงค่อยกรอกเพิ่ม
 *
 *   key       = ชื่อคอลัมน์/ฟิลด์ใน payload ตรง ๆ (assets.parking_motorcycle / parking_car)
 *   count_key = จำนวนคันของแบบนั้น (assets.parking_motorcycle_count / parking_car_count)
 *               ว่าง = จอดได้แต่ไม่ได้ระบุจำนวน
 * ========================================================================== */
export const PARKING_TYPES = [
  { value: "motorcycle", key: "parking_motorcycle", count_key: "parking_motorcycle_count", label: "parking_motorcycle", icon: "" },
  { value: "car", key: "parking_car", count_key: "parking_car_count", label: "parking_car", icon: "" },
];

/* ประเภทที่จอดที่ทรัพย์ใบนี้ติ๊กไว้ — คืนเป็น meta เพื่อให้ผู้เรียกเลือกใช้ label/icon เองได้
   (ไฟล์นี้ไม่ import t() เพราะถูกใช้ทั้งฝั่งแอดมินและหน้าเว็บลูกค้า) */
export const parkingTypes = (data) => PARKING_TYPES.filter((e) => !!data?.[e.key]);

/* จำนวนคันของประเภทนั้น — 0 = จอดได้แต่ไม่ได้ระบุจำนวน ผู้เรียกจึงเช็กก่อนค่อยเอาไปต่อท้าย label */
export const parkingCount = (data, item) => Number(data?.[item.count_key]) || 0;

/* ============================================================================
 * ระยะสัญญาเช่า — ตัวเลข 0-11 คู่กับหน่วยเดือน/ปี (เช่น 6 เดือน / 1 ปี)
 * value ของหน่วยต้องตรงกับ CONTRACT_UNITS ฝั่ง backend (asset.constant.js)
 * เลือกได้ถึง 11 เท่านั้น เพราะ 12 เดือนขึ้นไปให้สลับหน่วยเป็นปีแทน
 * ========================================================================== */
export const CONTRACT_TERMS = Array.from({ length: 12 }, (_, index) => String(index));

export const CONTRACT_UNITS = [
  { value: "month", label: "month_unit" },
  { value: "year", label: "year_unit" },
];

export const contractUnitMeta = (value) => CONTRACT_UNITS.find((e) => e.value === value) || CONTRACT_UNITS[0];

/* สิ่งอำนวยความสะดวกในห้อง — เรียงตามที่แสดงในตาราง 2 คอลัมน์ของฟอร์ม
   value ถูกเก็บลง assets.amenities (JSONB) ตรง ๆ ห้ามแก้ค่าเดิม ไม่งั้นทรัพย์ที่บันทึกไว้แล้ว
   จะจับคู่ไม่ติดแล้วหายไปจากหน้าประกาศ (เพิ่มรายการใหม่ต่อท้ายกลุ่มได้ตามปกติ)
   ไอคอนต้องเป็นรหัสที่มีอยู่จริงในฟอนต์ fa-light-300 (FontAwesome 5) ไม่งั้นจะขึ้นเป็นช่องว่าง */
export const AMENITIES = [
  /* ---- เครื่องใช้ไฟฟ้า ---- */
  { value: "washer", label: "amenity_washer", icon: "\uf553" },
  { value: "dryer", label: "amenity_dryer", icon: "\uf021" },
  { value: "tv", label: "amenity_tv", icon: "\uf26c" },
  { value: "smart_tv", label: "amenity_smart_tv", icon: "\uf144" },
  { value: "aircon", label: "amenity_aircon", icon: "\uf2dc" },
  { value: "water_heater", label: "amenity_water_heater", icon: "\uf2cc" },

  /* ---- ครัว ---- */
  { value: "fridge", label: "amenity_fridge", icon: "\uf76b" },
  { value: "microwave", label: "amenity_microwave", icon: "\uf466" },
  { value: "oven", label: "amenity_oven", icon: "\uf7e4" },
  { value: "stove", label: "amenity_stove", icon: "\uf0e7" },
  { value: "hood", label: "amenity_hood", icon: "\uf72e" },
  { value: "dishwasher", label: "amenity_dishwasher", icon: "\uf2e7" },
  { value: "water_purifier", label: "amenity_water_purifier", icon: "\uf0b0" },

  /* ---- ระบบ / ความปลอดภัย ---- */
  { value: "wifi", label: "amenity_wifi", icon: "\uf1eb" },
  { value: "doorlock", label: "amenity_doorlock", icon: "\uf30d" },

  /* ---- เฟอร์นิเจอร์ (มี/ไม่มี ทั้งห้อง เลือกที่หัวข้อ "เฟอร์นิเจอร์" แยกต่างหาก) ---- */
  { value: "bed", label: "amenity_bed", icon: "\uf236" },
  { value: "wardrobe", label: "amenity_wardrobe", icon: "\uf52a" },
  { value: "dining_table", label: "amenity_dining_table", icon: "\uf6c0" },
  { value: "desk", label: "amenity_desk", icon: "\uf108" },

  /* ---- พื้นที่ใช้สอยเพิ่มเติม ---- */
  { value: "bathtub", label: "amenity_bathtub", icon: "\uf2cd" },
  { value: "balcony", label: "amenity_balcony", icon: "\uf2d0" },
  { value: "storage", label: "amenity_storage", icon: "\uf468" },

  // "อื่น ๆ" ต้องอยู่ท้ายสุดเสมอ — ปุ่ม "เลือกทั้งหมด" ข้ามข้อนี้ (ดู BULK_AMENITIES ใน asset_create.js)
  { value: "other", label: "amenity_other", icon: "\uf141" },
];

/* ============================================================================
 * การตกแต่ง / เฟอร์นิเจอร์ — เลือกได้อย่างละ 1 ค่า (ไม่ใช่ติ๊กหลายข้อ)
 * เก็บเป็นรหัสในคอลัมน์ decoration / furnished ของ assets
 * ========================================================================== */
export const DECORATIONS = [
  { value: "full", label: "decoration_full" },
  { value: "partial", label: "decoration_partial" },
  { value: "none", label: "decoration_none" },
];

export const FURNISH_OPTIONS = [
  { value: "furnished", label: "furnish_furnished" },
  { value: "unfurnished", label: "furnish_unfurnished" },
  { value: "partial", label: "furnish_partial" },
];

export const decorationMeta = (value) => DECORATIONS.find((e) => e.value === value);
export const furnishMeta = (value) => FURNISH_OPTIONS.find((e) => e.value === value);

/* จุดเด่น / แท็ก ที่เลือกได้ (ผู้ใช้เพิ่มแท็กเองได้อีก) */
export const TAGS = [
  { value: "smoking", label: "tag_smoking" },
  { value: "corner", label: "tag_corner" },
  { value: "city_view", label: "tag_city_view" },
  { value: "near_mrt", label: "tag_near_mrt" },
  { value: "near_bts", label: "tag_near_bts" },
  { value: "near_university", label: "tag_near_university" },
  { value: "ready", label: "tag_ready" },
  { value: "renovated", label: "tag_renovated" },
  { value: "new_building", label: "tag_new_building" },
  { value: "built_in", label: "tag_built_in" },
  { value: "duplex", label: "tag_duplex" },
  { value: "penthouse", label: "tag_penthouse" },
  { value: "many_parking", label: "tag_many_parking" },
  { value: "pool_view", label: "tag_pool_view" },
  { value: "invest", label: "tag_invest" },
];

/* จุดสนใจใกล้เคียง — ประเภทและไอคอนต้องตรงกับที่หน้ารายละเอียดฝั่งลูกค้าใช้แสดง
   (page/homepage/components/PropertyBody.js → NEARBY_ICON) และ value ต้องตรงกับ
   NEARBY_TYPES ฝั่ง backend (modules/admin/asset/asset.constant.js) */
export const NEARBY_TYPES = [
  { value: "transit", label: "nearby_type_transit", icon: "" },
  { value: "mall", label: "nearby_type_mall", icon: "" },
  { value: "hospital", label: "nearby_type_hospital", icon: "" },
  { value: "school", label: "nearby_type_school", icon: "" },
  { value: "park", label: "nearby_type_park", icon: "" },
  { value: "other", label: "nearby_type_other", icon: "" },
];

/* ส่วนกลางของโครงการ — มาจากข้อมูล master ของโครงการ ไม่ใช่ของทรัพย์รายห้อง
   TODO: ย้ายไปผูกกับตารางโครงการเมื่อเมนู "โครงการ" ฝั่งแอดมินพร้อมใช้งาน */
export const FACILITIES = [
  { value: "pool", label: "facility_pool", icon: "\uf5c5" },
  { value: "fitness", label: "facility_fitness", icon: "\uf44b" },
  { value: "co_working", label: "facility_co_working", icon: "\uf109" },
  { value: "garden", label: "facility_garden", icon: "\uf1bb" },
  { value: "security", label: "facility_security", icon: "\uf3ed" },
  { value: "parking", label: "facility_parking", icon: "\uf540" },
  { value: "cctv", label: "facility_cctv", icon: "\uf030" },
  { value: "lift", label: "facility_lift", icon: "\uf338" },
  { value: "shuttle", label: "facility_shuttle", icon: "\uf5b6" },
  { value: "laundry", label: "facility_laundry", icon: "\uf553" },
  { value: "sauna", label: "facility_sauna", icon: "\uf2c9" },
  { value: "playground", label: "facility_playground", icon: "\uf1ae" },
  { value: "clubhouse", label: "facility_clubhouse", icon: "\uf1ad" },
  // "อื่น ๆ" ต้องอยู่ท้ายสุด — ติ๊กแล้วเปิดช่องพิมพ์ชื่อส่วนกลางเพิ่ม (facility_other)
  { value: "other", label: "facility_other", icon: "" },
];

export const nearbyMeta = (value) => NEARBY_TYPES.find((e) => e.value === value) || NEARBY_TYPES[NEARBY_TYPES.length - 1];
export const amenityMeta = (value) => AMENITIES.find((e) => e.value === value);
export const facilityMeta = (value) => FACILITIES.find((e) => e.value === value);
export const directionMeta = (value) => DIRECTIONS.find((e) => e.value === value);
export const tagMeta = (value) => TAGS.find((e) => e.value === value);

/* ============================================================================
 * ข้อมูล master ของเมนู "ประเภททรัพย์"  (/ad/property-types)
 *
 * ประเภททรัพย์ถูกใช้ซ้ำหลายจุดในระบบ — ตัวกรองหน้ารวมทรัพย์ ฟอร์มเพิ่ม/แก้ไขทรัพย์
 * รายงาน และเว็บหน้าบ้าน จึงเก็บไว้ไฟล์เดียวกับ PROPERTY_TYPES และ "สร้างจาก"
 * PROPERTY_TYPES โดยตรง เพื่อกันไม่ให้ข้อมูล master กับตัวเลือกในระบบหลุดจากกัน
 * ========================================================================== */

/* ชุดไอคอน / สีการ์ด ให้เลือกตอนเพิ่มประเภททรัพย์ใหม่ */
export const TYPE_ICONS = ["\uf015", "\uf1ad", "\uf64f", "\uf0b1", "\uf54e", "\uf494", "\uf1bb", "\uf66f", "\uf5a0", "\uf2b5"];

export const TYPE_GRADS = [
  "linear-gradient(150deg,#dfeaf5,#c3d5e8)",
  "linear-gradient(150deg,#e9efe3,#d3e2c6)",
  "linear-gradient(150deg,#f1ebe2,#dfd3c3)",
  "linear-gradient(150deg,#eae7f3,#d4cfe6)",
  "linear-gradient(150deg,#e4eef0,#c7dde0)",
  "linear-gradient(150deg,#f3e8e8,#e2cccc)",
  "linear-gradient(150deg,#eceef0,#d2d7dc)",
  "linear-gradient(150deg,#e7f1e4,#cbe3c6)",
];

/* ข้อมูล master รายประเภท (ชื่อ ไทย/อังกฤษ คำอธิบาย ลำดับ สถานะ) อยู่ในฐานข้อมูลแล้ว
   ดึงผ่าน GET admin/asset-categories — ไฟล์นี้เหลือเฉพาะชุดไอคอน/สีให้เลือกตอนสร้าง */
